// /analytika/prehled — souhrn webu, reklam a produktů na jedné obrazovce (22. 9. 2026).
// Podoba schválená v mockupu „Analytika v adminu" 22. 9. 2026.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, RefreshCw, Route, Wallet, Signpost, AlertTriangle, ArrowUpRight, Zap } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Dlazdice, ErrorNote, SkeletonCards, Hlaska, Zalozky } from "../../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, SkupinaPrehledu, RadkyPrehledu, RadekPrehledu, Pilulka, HlaskaDlazdice, AkceHlavicky,
} from "../../components/PrehledDlazdice.jsx";
import { useObdobi, VyberObdobi, useAnalytika, cislo, kc, zmena, vysledekText } from "./spolecne.jsx";
import { ikonaProduktu } from "./ikony.js";
import ZivyPrehled, { useZivaData } from "./Zive.jsx";

// Režim Přehledu: za období, nebo živě (mockup „Živě v Analytice", varianta B).
// Pamatuje se, ať se po návratu otevře, co bylo naposledy.
const KLIC_REZIMU = "fw40.analytika.rezim";


// Trial automat: zkušební trénink, který si žena po dotazníku sama vybere
// a hned zaplatí kartou. Kartu se vyplatí mít v Přehledu, protože je to
// jediná cesta na webu, která dělá tržbu bez hovoru.
const DNY_ZKR = ["po", "út", "st", "čt", "pá", "so", "ne"];
const podil = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);

function KartaTrialAutomat({ trial }) {
  const t = trial || {};
  const kroky = [
    { popisek: "Odeslaný dotazník", hodnota: t.dotaznik || 0, napoveda: "Žena odeslala dotazník na webu. Základ, ze kterého se počítají procenta." },
    { popisek: "Kliklo na nabídku", hodnota: t.nabidka || 0, napoveda: "Na stránce Děkujeme klikla na kartu zkušebního tréninku." },
    { popisek: "Vybralo termín", hodnota: t.terminy_krok || 0 },
    { popisek: "Došlo na platbu", hodnota: t.platba || 0, napoveda: "Termín se jí zablokoval na 20 minut a otevřel se platební formulář." },
    { popisek: "Zaplatilo kartou", hodnota: t.zaplaceno || 0 },
  ];
  const zaklad = kroky[0].hodnota || kroky[1].hodnota || 0;

  let propad = null;
  if (kroky[1].hodnota > 0) {
    for (let i = 1; i < kroky.length; i++) {
      const ztrata = podil(kroky[i - 1].hodnota, zaklad) - podil(kroky[i].hodnota, zaklad);
      if (ztrata > 0 && (!propad || ztrata > propad.ztrata)) {
        propad = { z: kroky[i - 1].popisek, na: kroky[i].popisek, ztrata: Math.round(ztrata * 10) / 10 };
      }
    }
  }

  const nejcastejsi = (t.terminy || [])[0];
  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Zap}
        nazev="Trial automat"
        podtitulek="Zkušební trénink rezervovaný a zaplacený na webu"
        odznak={vysledekText(t.zaplaceno || 0, "nakup")}
        odznakTon={t.zaplaceno ? "bily" : "cerveny"}
      />
      <SkupinaPrehledu ikona={Route} nazev="Cesta k zaplacenému tréninku" />
      <RadkyPrehledu>
        {kroky.map((k, i) => (
          <RadekPrehledu
            key={k.popisek}
            popisek={k.popisek}
            napoveda={k.napoveda}
            hodnota={cislo(k.hodnota)}
            doplnek={i > 0 && zaklad ? `${podil(k.hodnota, zaklad)} % z dotazníku` : null}
            tonHodnoty={i === kroky.length - 1 && k.hodnota ? "text-emerald-700" : "text-slate-900"}
            pruh={zaklad ? podil(k.hodnota, zaklad) : 0}
          />
        ))}
      </RadkyPrehledu>
      <SkupinaPrehledu ikona={Wallet} nazev="Peníze a termíny" />
      <RadkyPrehledu>
        <RadekPrehledu popisek="Tržba" hodnota={kc(t.trzba || 0)} doplnek={t.potvrzene ? `${t.potvrzene} potvrzených` : null} />
        <RadekPrehledu
          popisek="Nedokončené platby"
          napoveda="Držené termíny, které po 20 minutách propadly. Kontakt se vrátil sales s poznámkou, ať se zeptá, co platbě zabránilo."
          hodnota={cislo(t.nedokoncene || 0)}
          doplnek={t.drzene ? `${t.drzene} se právě drží` : null}
          tonHodnoty={t.nedokoncene ? "text-rose-700" : "text-slate-900"}
        />
        <RadekPrehledu
          popisek="Nejčastější termín"
          hodnota={nejcastejsi ? `${DNY_ZKR[(nejcastejsi.den || 1) - 1]} ${nejcastejsi.cas}` : "zatím nic"}
          doplnek={nejcastejsi ? `${nejcastejsi.pocet}×` : null}
        />
      </RadkyPrehledu>
      {propad && (
        <HlaskaDlazdice
          ton="zluta"
          ikona={AlertTriangle}
          nadpis="Kde to padá"
          text={`Mezi kroky ${propad.z} a ${propad.na} mizí ${String(propad.ztrata).replace(".", ",")} % žen. Tuhle část projdi první.`}
        />
      )}
    </KartaPrehledu>
  );
}

export default function AnalytikaPrehled() {
  const [rezim, setRezimStav] = useState(() => {
    try { return localStorage.getItem(KLIC_REZIMU) === "zive" ? "zive" : "obdobi"; } catch { return "obdobi"; }
  });
  const setRezim = (r) => {
    setRezimStav(r);
    try { localStorage.setItem(KLIC_REZIMU, r); } catch { /* nevadí */ }
  };
  const zive = rezim === "zive";
  const obdobi = useObdobi();
  const { data: obdobiData, loading, error, refetch, refreshing } = useAnalytika(obdobi.rozsah, [], !zive);
  const ziva = useZivaData(zive);
  const data = zive ? null : obdobiData;
  const navigate = useNavigate();
  const s = data?.souhrn;
  const p = data?.souhrnPred;

  return (
    <Stranka>
      <GradientHeader
        icon={LineChart}
        title="Analytika"
        napoveda="Souhrn webu, reklam a produktů za vybrané období. Změny se počítají proti stejně dlouhému období těsně předtím. Čísla webu jsou z vlastního měření bez cookies, útrata z Mety a z Google Ads."
        loading={zive ? ziva.nacita : refreshing}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: zive ? ziva.nacti : refetch, disabled: zive ? ziva.nacita : refreshing }]}
      />
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="flex items-center">
          <Zalozky stitek="Režim" aria-label="Režim" value={rezim} onChange={setRezim}
            items={[{ key: "obdobi", label: "Za období" }, { key: "zive", label: "Živě" }]} />
        </div>
        {!zive && <VyberObdobi obdobi={obdobi} />}
      </div>

      {zive && <ZivyPrehled data={ziva.data} chyba={ziva.chyba} />}
      {zive && !ziva.data && !ziva.chyba && <SkeletonCards count={3} />}

      {!zive && error && <ErrorNote onRetry={refetch}>Analytiku se nepodařilo načíst: {error}</ErrorNote>}
      {!zive && loading && !data && <SkeletonCards count={4} />}
      {data?.chyby?.length > 0 && (
        <Hlaska ton="pozor" title="Část dat chybí">{data.chyby.join(". ")}</Hlaska>
      )}

      {s && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
            <Dlazdice
              popisek="Návštěvnice webu"
              hodnota={cislo(s.navstevnice)}
              ton="znacka"
              doplnky={zmena(s.navstevnice, p?.navstevnice) && <Pilulka>{zmena(s.navstevnice, p?.navstevnice)}</Pilulka>}
            />
            <Dlazdice
              popisek="Nákupy"
              hodnota={cislo(s.nakupy)}
              ton="ok"
              doplnky={<Pilulka>{kc(s.trzba)}</Pilulka>}
            />
            <Dlazdice
              popisek="Reklama celkem"
              hodnota={s.metaNedostupna ? "nedostupné" : kc(s.reklama)}
              ton="neutralni"
              doplnky={<Pilulka>Meta {s.metaNedostupna ? "neodpověděla" : cislo(s.reklamaMeta)}<span aria-hidden className="h-3 w-px bg-current opacity-40" />Google {data.google?.propojeno ? cislo(s.reklamaGoogle) : "nepropojeno"}</Pilulka>}
            />
            <Dlazdice
              popisek="Cena za nákup"
              hodnota={s.metaNedostupna ? "nedostupné" : s.cenaZaNakup != null ? kc(s.cenaZaNakup) : s.reklama > 0 ? "bez nákupu" : "—"}
              ton={!s.metaNedostupna && s.reklama > 0 && !s.nakupy ? "problem" : "znacka"}
              hodnotaTon={!s.metaNedostupna && s.reklama > 0 && !s.nakupy ? "problem" : undefined}
              doplnky={s.navratnost != null && <Pilulka>návratnost {String(s.navratnost).replace(".", ",")}×</Pilulka>}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {(data.produkty || []).map((pr) => {
              const Ikona = ikonaProduktu(pr.ikona);
              const cervene = pr.reklama > 0 && pr.vysledky === 0 && !pr.kontaktyMeta;
              return (
                <KartaPrehledu key={pr.klic}>
                  <HlavickaDlazdice
                    ikona={Ikona}
                    nazev={pr.nazev}
                    odznak={vysledekText(pr.vysledky, pr.typVysledku)}
                    odznakTon={cervene ? "cerveny" : "bily"}
                    akce={pr.prodejka?.length ? <AkceHlavicky ikona={ArrowUpRight} popis="Otevřít detail produktu" onClick={() => navigate(`/analytika/produkty?produkt=${pr.klic}`)} /> : null}
                  />
                  <SkupinaPrehledu ikona={Route} nazev="Cesta k výsledku" />
                  <RadkyPrehledu>
                    {pr.prodejka?.length > 0 && <RadekPrehledu popisek="Prodejní stránka" hodnota={cislo(pr.stranka)} doplnek={pr.lokalita ? "obě lokality" : null} />}
                    {pr.lokalita && <RadekPrehledu popisek="Z toho vybrala tuhle lokalitu" hodnota={cislo(pr.strankaLokalita)} />}
                    {pr.klik != null && <RadekPrehledu popisek="Klik k nákupu" hodnota={cislo(pr.klik)} />}
                    {pr.pokladna != null && <RadekPrehledu popisek="Pokladna" hodnota={cislo(pr.pokladna)} tonHodnoty={cervene && !pr.pokladna ? "text-rose-700" : "text-slate-900"} />}
                    <RadekPrehledu popisek={pr.typVysledku === "lead" ? "Odeslaný formulář" : "Nákupy"} hodnota={cislo(pr.vysledky)} tonHodnoty={pr.vysledky ? "text-emerald-700" : "text-slate-900"} />
                    {pr.kontaktyMeta > 0 && <RadekPrehledu popisek="Kontakty z formuláře v Metě" hodnota={cislo(pr.kontaktyMeta)} tonHodnoty="text-emerald-700" />}
                  </RadkyPrehledu>
                  <SkupinaPrehledu ikona={Wallet} nazev="Peníze" />
                  <RadkyPrehledu>
                    {pr.trzbaZnama && <RadekPrehledu popisek="Tržba" hodnota={kc(pr.trzba)} />}
                    <RadekPrehledu popisek="Reklama" hodnota={pr.reklamaNedostupna ? "nedostupné" : kc(pr.reklama)} />
                    {pr.reklama > 0 && !pr.reklamaNedostupna && (
                      <RadekPrehledu
                        popisek={pr.typVysledku === "lead" ? "Cena za kontakt" : "Cena za nákup"}
                        hodnota={pr.cenaZaVysledek != null ? kc(pr.cenaZaVysledek) : "bez výsledku"}
                        tonHodnoty={pr.cenaZaVysledek == null ? "text-rose-700" : "text-slate-900"}
                      />
                    )}
                  </RadkyPrehledu>
                  {cervene && (
                    <HlaskaDlazdice
                      ikona={AlertTriangle}
                      nadpis="Reklama bez výsledku"
                      text={`Za ${kc(pr.reklama)} žádný ${pr.typVysledku === "lead" ? "kontakt" : "nákup"}. Podívej se na kampaně v Reklamách.`}
                    />
                  )}
                </KartaPrehledu>
              );
            })}
          </div>

          {data.trial && (data.trial.dotaznik > 0 || data.trial.zaplaceno > 0 || data.trial.drzene > 0) && (
            <KartaTrialAutomat trial={data.trial} />
          )}

          <KartaPrehledu>
            <HlavickaDlazdice ikona={Signpost} nazev="Odkud přišly" odznak={`${(data.zdroje || []).length} zdrojů`} />
            <RadkyPrehledu>
              {(data.zdroje || []).map((z) => {
                const doplnek = [z.nakupy ? vysledekText(z.nakupy, "nakup") : null, z.kontakty ? vysledekText(z.kontakty, "lead") : null].filter(Boolean).join(", ");
                return (
                  <RadekPrehledu
                    key={z.klic}
                    popisek={z.skupina}
                    hodnota={cislo(z.navstevnice)}
                    doplnek={doplnek || null}
                    pruh={s.navstevnice ? (z.navstevnice / s.navstevnice) * 100 : 0}
                  />
                );
              })}
            </RadkyPrehledu>
            <HlaskaDlazdice
              ton="zluta"
              ikona={AlertTriangle}
              nadpis="Jak číst přímý příchod"
              text="Měření je bez cookies. Návštěvnice se pozná jen v rámci jednoho dne, takže návrat druhý den nebo odkaz z Telegramu, SMS či WhatsAppu se ukáže jako přímý příchod."
            />
          </KartaPrehledu>
        </>
      )}
    </Stranka>
  );
}
